
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavigationContextType {
  goBack: () => void;
  previousPath: string | null;
}

const NavigationContext = createContext<NavigationContextType>({
  goBack: () => {},
  previousPath: null,
});

export const useNavigation = () => useContext(NavigationContext);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [history, setHistory] = useState<string[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const isNavigatingBack = useRef(false);

  useEffect(() => {
    // Only add to history if this is a new location and not navigating back
    if (!isNavigatingBack.current && 
        (history.length === 0 || history[history.length - 1] !== location.pathname)) {
      setHistory(prev => [...prev, location.pathname]);
    }
    
    // Reset the navigating back flag
    isNavigatingBack.current = false;
  }, [location.pathname, history]);

  const goBack = () => {
    if (history.length > 1) {
      // Set the flag to indicate we're navigating back
      isNavigatingBack.current = true;
      
      // Remove current page from history
      const newHistory = [...history];
      newHistory.pop();
      
      // Get the previous page
      const previousPath = newHistory[newHistory.length - 1];
      
      // Update history and navigate
      setHistory(newHistory);
      navigate(previousPath);
    } else {
      // Default fallback if no history
      navigate('/');
    }
  };

  return (
    <NavigationContext.Provider
      value={{
        goBack,
        previousPath: history.length > 1 ? history[history.length - 2] : null,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};
